import { createAsyncThunk } from "@reduxjs/toolkit";

import { SurveyStatus, type ClientSurvey } from "@fitness-app/data-models/entities/ClientSurvey";

import { type AsyncThunkCreator } from "../../../index";
import { TRAINEE_SURVEYS_REDUCER_NAME } from "../types";

export type SurveyListFilter = "toFill" | "sent" | "all";

// Surveys a client is allowed to see. Scheduled/new instances are future recurring cycles —
// answering them before delivery breaks the recurrence chain and skips answer automations.
const CLIENT_VISIBLE_STATUSES = [
  SurveyStatus.Sent,
  SurveyStatus.Open,
  SurveyStatus.PartiallyFulfilled,
  SurveyStatus.Fulfilled,
  SurveyStatus.Archived,
];

const FILLABLE_STATUSES = [SurveyStatus.Sent, SurveyStatus.Open, SurveyStatus.PartiallyFulfilled];

type Payload = {
  traineeId: string;
  /**
   * Filter by submission state, used by client-facing lists (web + mobile). Every explicit value
   * restricts results to statuses a client may see. When omitted (trainer panel) all rows are
   * returned, including scheduled future cycles.
   */
  filter?: SurveyListFilter;
  /** When both offset and limit are provided the query is paginated; otherwise all rows are returned. */
  offset?: number;
  limit?: number;
};

export const fetchTraineeSurveys = createAsyncThunk<ClientSurvey[], Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_SURVEYS_REDUCER_NAME}/fetchTraineeSurveys`,
  async ({ traineeId, filter, offset, limit }, { rejectWithValue, extra: { db } }) => {
    let query = db
      .from("client_survey")
      .select("*")
      .eq("traineeId", traineeId)
      .order("createdAt", { ascending: false });

    if (filter === "toFill") {
      query = query.is("answeredAt", null).in("status", FILLABLE_STATUSES);
    } else if (filter === "sent") {
      query = query.not("answeredAt", "is", null).in("status", CLIENT_VISIBLE_STATUSES);
    } else if (filter === "all") {
      // An archived row that was never delivered nor answered (a client archived before the send,
      // a technical copy of a duplicated cycle - TRE-2513) is nothing the client ever saw.
      query = query
        .in("status", CLIENT_VISIBLE_STATUSES)
        .or(`status.neq.${SurveyStatus.Archived},sentAt.not.is.null,answeredAt.not.is.null`);
    }

    if (typeof offset === "number" && typeof limit === "number") {
      query = query.range(offset, offset + limit - 1);
    }

    const { error, data } = await query.returns<ClientSurvey[]>();

    if (error) {
      return rejectWithValue(error.message);
    }

    return data;
  },
);
